import {
  Text,
  Button,
  Card,
  TextInputField,
  Banner,
  GreyWhite,
  Primary14SemiBold,
  GreyGrey60,
  Primary13Regular,
  IconEyeOpen,
  IconEyeSlash,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import Spinner from 'components/Loading/Spinner'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useEffect, useState, useCallback, useMemo } from 'react'

type FormValues = {
  apiKey: string | undefined
}

interface Props {
  existingCredentials: FormValues
  onConnectionSuccess: (credentials: { apiKey: string }) => void
  onCredentialsChange: (credentials: FormValues) => void
}

const AnrokAuthenticationCard = ({
  existingCredentials,
  onConnectionSuccess,
  onCredentialsChange
}: Props) => {
  const isFirstTimeSetup = useMemo(
    () => !existingCredentials.apiKey,
    [existingCredentials.apiKey]
  )

  const [showApiKey, setShowApiKey] = useState(false)
  const [justValidatedKey, setJustValidatedKey] = useState<string | null>(null)
  const [credentialValidationResult, setCredentialValidationResult] = useState<{
    status: 'success' | 'error' | null
    message?: string
  }>({ status: null })

  useEffect(() => {
    if (existingCredentials.apiKey) {
      setJustValidatedKey(null)
    }
  }, [existingCredentials.apiKey])

  const { fields, queries } = useForm({
    value: existingCredentials,
    fieldConfiguration: [
      {
        property: 'apiKey',
        validation: [required]
      }
    ],
    onChange: onCredentialsChange
  })

  const {
    isError,
    isSuccess,
    isPending: isTestingCredentials,
    reset: resetTestCredentials,
    mutate: testCredentials
  } = useMutation(dashboardv99990101Client.postTestAnrokCredentials, {
    onSuccess: (result, variables) => {
      if (result?.isValid && variables.apiKey) {
        setJustValidatedKey(variables.apiKey)
        setCredentialValidationResult({
          status: 'success',
          message:
            'Credentials verified. Your Anrok API key has been validated.'
        })
        onConnectionSuccess({
          apiKey: variables.apiKey
        })
      } else {
        setCredentialValidationResult({
          status: 'error',
          message:
            result?.message ||
            'Invalid credentials. Please check your API key and try again.'
        })
      }
    },
    onError: () => {
      setCredentialValidationResult({
        status: 'error',
        message:
          'Connection failed. Please check your network connection and try again.'
      })
    }
  })

  useEffect(() => {
    if (!isFirstTimeSetup) {
      resetTestCredentials()
    }
  }, [isFirstTimeSetup, resetTestCredentials])

  const handleApiKeyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isSuccess || isError || credentialValidationResult.status) {
        resetTestCredentials()
        setCredentialValidationResult({ status: null })
      }
      fields.apiKey.onChange(event?.target?.value)
    },
    [
      isSuccess,
      isError,
      credentialValidationResult.status,
      resetTestCredentials,
      fields.apiKey
    ]
  )

  const isTestedOrSaved = useMemo(() => {
    return (
      credentialValidationResult.status === 'success' ||
      (!!existingCredentials.apiKey &&
        fields.apiKey.value === existingCredentials.apiKey)
    )
  }, [
    credentialValidationResult.status,
    existingCredentials.apiKey,
    fields.apiKey.value
  ])

  const apiKeyDisplayValue = useMemo(() => {
    if (credentialValidationResult.status !== 'success')
      return fields.apiKey.value
    if (existingCredentials.apiKey) return existingCredentials.apiKey
    if (justValidatedKey) {
      const key = justValidatedKey
      if (key.length <= 8) return '*'.repeat(key.length)
      return `${key.slice(0, 4)}${'*'.repeat(key.length - 8)}${key.slice(-4)}`
    }
    return fields.apiKey.value
  }, [
    credentialValidationResult.status,
    existingCredentials.apiKey,
    justValidatedKey,
    fields.apiKey.value
  ])

  const handleTestCredentials = useCallback(() => {
    if (fields.apiKey.value) {
      void testCredentials({
        apiKey: fields.apiKey.value
      })
    }
  }, [fields.apiKey.value, testCredentials])

  const toggleShowApiKey = useCallback(() => {
    setShowApiKey(prev => !prev)
  }, [])

  return (
    <Card>
      <Flex flexDirection="column" gap="24px" data-testid="anrokAuth.card">
        <Text {...Primary14SemiBold}>Anrok authentication</Text>

        <Box>
          <Flex gap="12px" alignItems="flex-end">
            <Box flex={1} minWidth={showApiKey ? '550px' : '450px'}>
              <TextInputField
                label="API Key"
                data-testid="anrokAuth.apiKey"
                value={apiKeyDisplayValue}
                onChange={handleApiKeyChange}
                validationErrors={fields.apiKey.validationErrors}
                placeholder="Enter your Anrok API key"
                type={showApiKey ? 'text' : 'password'}
                fullWidth
                suffix={
                  <Tooltip
                    label={showApiKey ? 'Hide API key' : 'Show API key'}
                    placement="top"
                    shouldWrapChildren={false}
                  >
                    <Button
                      data-testid="anrokAuth.toggleApiKey"
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                      icon={showApiKey ? <IconEyeSlash /> : <IconEyeOpen />}
                      variant="unstyled"
                      size="16px"
                      onClick={toggleShowApiKey}
                    />
                  </Tooltip>
                }
                suffixStyles={{ pointerEvents: 'auto' }}
              />
            </Box>

            <Box minWidth="140px">
              <Button
                data-testid="anrokAuth.testCredentials"
                onClick={handleTestCredentials}
                disabled={
                  isTestingCredentials || !queries.isValid || isTestedOrSaved
                }
                leadingIcon={
                  isTestingCredentials ? (
                    <Spinner color={GreyWhite} height="16px" width="16px" />
                  ) : undefined
                }
                variant="primary"
              >
                {isTestingCredentials ? 'Checking...' : 'Test credentials'}
              </Button>
            </Box>
          </Flex>

          <Text {...Primary13Regular} mt="8px" color={GreyGrey60}>
            Enter your Anrok API key to connect your account.
          </Text>
        </Box>

        {credentialValidationResult.status === 'success' && (
          <Banner data-testid="anrokAuth.banner.success" variant="success">
            {credentialValidationResult.message}
          </Banner>
        )}

        {credentialValidationResult.status === 'error' && (
          <Banner data-testid="anrokAuth.banner.error" variant="error">
            {credentialValidationResult.message}
          </Banner>
        )}
      </Flex>
    </Card>
  )
}

export default AnrokAuthenticationCard
